import SubHeaders from '@/components/Headers/SubHeaders'
import { useCustomerCreate } from '@/modules/Customer/hooks/use-customer-create'
import { paths } from '@/utils/path'
import { Building2, Camera, FileText, Mail, User } from 'lucide-react'
import { FormProvider } from 'react-hook-form'
import { Button } from 'vnetwork-ui'
import StepIndicator from './StepIndicator'
import {
    StepEmail,
    StepBusinessInfo,
    StepContactInfo,
    StepAdditionalInfo,
} from './steps'

/**
 * Customer Create Page
 * Multi-step form for creating new customers
 */
const CustomerCreate = () => {
    const {
        methods,
        control,
        setValue,
        currentStep,
        handleNext,
        handleBack,
        emailChecking,
        emailValid,
        emailValue,
        emailExistsResult,
        phoneChecking,
        phoneMatchesLead,
        phoneExistsResult,
        customerType,
        customerSource,
        handleNoOptionCompany,
        handleCompanySelected,
        isScanning,
        scanInputRef,
        handleQuickScan,
        isPending,
        t,
    } = useCustomerCreate()

    const steps = [
        { label: t('check-email'), icon: <Mail className="w-4 h-4" /> },
        {
            label: t('add-customer-step-1'),
            icon: <Building2 className="w-4 h-4" />,
        },
        { label: t('add-customer-step-2'), icon: <User className="w-4 h-4" /> },
        {
            label: t('add-customer-step-3'),
            icon: <FileText className="w-4 h-4" />,
        },
    ]

    return (
        <FormProvider {...methods}>
            <div>
                <SubHeaders
                    actions={[
                        {
                            icon: <Camera className="w-4 h-4" />,
                            label: t('scan_card.quick_scan'),
                            variant: 'outlined',
                            loading: isScanning,
                            onClick: () => scanInputRef.current?.click(),
                        },
                    ]}
                    breadcrumbs={[
                        {
                            path: paths.customersList,
                            title: t('qualified-prospects'),
                        },
                        {
                            path: paths.customerCreate,
                            title: t('add-customer'),
                        },
                    ]}
                />

                {/* Main form card */}
                <div
                    className="bg-white rounded-lg mt-4 shadow-sm"
                    data-tour="customer-form"
                >
                    {/* Step indicator */}
                    <StepIndicator steps={steps} currentStep={currentStep} />

                    {/* Step content */}
                    <div className="p-6 border-t">
                        {currentStep === 1 && (
                            <StepEmail
                                t={t}
                                control={control}
                                emailChecking={emailChecking}
                                emailValid={emailValid}
                                emailValue={emailValue}
                                emailExistsResult={emailExistsResult}
                            />
                        )}

                        {currentStep === 2 && (
                            <StepBusinessInfo
                                control={control}
                                setValue={setValue}
                                customerType={customerType}
                                customerSource={customerSource}
                                handleNoOptionCompany={handleNoOptionCompany}
                                handleCompanySelected={handleCompanySelected}
                                t={t}
                            />
                        )}

                        {currentStep === 3 && (
                            <StepContactInfo
                                control={control}
                                t={t}
                                phoneChecking={phoneChecking}
                                phoneMatchesLead={phoneMatchesLead}
                                phoneExistsResult={phoneExistsResult}
                            />
                        )}

                        {currentStep === 4 && (
                            <StepAdditionalInfo control={control} t={t} />
                        )}
                    </div>

                    {/* Action buttons */}
                    <div className="p-4 border-t flex items-center justify-end gap-4">
                        <Button
                            className="min-w-[120px]"
                            variant="outlined"
                            disabled={currentStep === 1 || isPending}
                            onClick={handleBack}
                        >
                            {t('comeback')}
                        </Button>
                        <Button
                            className="min-w-[120px]"
                            disabled={
                                isPending ||
                                (currentStep === 1 &&
                                    (!emailValid || !emailValue)) ||
                                (currentStep === 3 && phoneMatchesLead)
                            }
                            loading={isPending}
                            onClick={handleNext}
                            data-tour="submit-btn"
                        >
                            {currentStep === 4 ? t('create') : t('continue')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Hidden camera input for quick scan */}
            <input
                type="file"
                ref={scanInputRef}
                accept="image/jpeg,image/png"
                capture="environment"
                onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleQuickScan(file)
                }}
                className="hidden"
            />
        </FormProvider>
    )
}

export default CustomerCreate
